import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { AddDocsToDocChronologyResponseDto } from '../../../../backend/src/case/chronology/doc-chronology/dto/add-docs-to-doc-chron-response.dto';
import { TrackingEventsV2 } from '../../analytics/eventsV2';
import { trackCustomEvent } from '../../analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface AddDocsToDocChronologyParams {
  matterId: string;
  subsetId: string;
  docChronologyId: string;
  docIds: string[];
}

export const useAddDocsToDocChronology = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      matterId,
      subsetId,
      docChronologyId,
      docIds,
    }: AddDocsToDocChronologyParams): Promise<AddDocsToDocChronologyResponseDto> => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: { docIds },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/add-docs`,
        fetchConfig,
      );

      if (!response.ok) {
        throw new Error('Failed to add documents to document chronology');
      }

      return response.json();
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([QueryEntity.DocChronologies, { subsetId: variables.subsetId }]);
        queryClient.invalidateQueries([QueryEntity.DocChronologyDocs, { docChronologyId: variables.docChronologyId }]);

        const { addedCount, skippedCount, missingCount } = data;

        if (addedCount === 0 && skippedCount === 0 && missingCount > 0) {
          toast.error(`${missingCount} ${missingCount === 1 ? 'document' : 'documents'} not found in this subset`);
          return;
        }

        if (addedCount === 0) {
          toast.info('All documents already exist in this chronology');
          return;
        }

        const parts = [`${addedCount} ${addedCount === 1 ? 'document' : 'documents'} added to chronology`];
        if (skippedCount > 0) {
          parts.push(`${skippedCount} already ${skippedCount === 1 ? 'exists' : 'exist'}`);
        }
        if (missingCount > 0) {
          parts.push(`${missingCount} not found in this subset`);
        }

        toast.success(`${parts[0]}${parts.length > 1 ? ` (${parts.slice(1).join(', ')})` : ''}`);

        trackCustomEvent(TrackingEventsV2.DocChronAddDocs);
      },
      onError: () => {
        toast.error('Failed to add documents to chronology');
      },
    },
  );
};
